import React from 'react';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import type { HealthScoreBreakdown } from '@/types/supremeIntelligenceTypes';
import { formatCurrency, generateExecutiveSummary } from '@/utils/supreme-intelligence/pdfFormatting';

interface SupremeIntelligencePDFViewProps {
  formData: EnhancedUnifiedFormData;
  healthScore?: HealthScoreBreakdown;
  budgetData?: any;
  loanPrograms?: any[];
  savingsData?: any;
  creditActions?: any[];
}

export const SupremeIntelligencePDFView: React.FC<SupremeIntelligencePDFViewProps> = ({
  formData,
  healthScore,
  budgetData,
  loanPrograms,
  savingsData,
  creditActions,
}) => {
  return (
    <div
      data-pdf-content
      style={{
        position: 'absolute',
        left: '-9999px',
        width: '800px',
        background: 'white',
        padding: '40px',
        fontFamily: 'Arial, sans-serif',
        color: '#1f2937',
      }}
    >
      {/* Header */}
      <div style={{ borderBottom: '3px solid #6366f1', paddingBottom: '20px', marginBottom: '30px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 10px 0' }}>
          Supreme Intelligence Analysis
        </h1>
        <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
          Generated {new Date().toLocaleDateString()} • EquiForge™ Platform
        </p>
      </div>

      {/* Executive Summary */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
          Executive Summary
        </h2>
        <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
          {generateExecutiveSummary(formData, healthScore)}
        </p>
      </section>

      {/* Financial Health Score */}
      {healthScore && (
        <section style={{ marginBottom: '30px', padding: '20px', background: '#f9fafb', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
            Financial Health Score
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '48px', fontWeight: 'bold', color: healthScore.color }}>
              {healthScore.totalScore}
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                {healthScore.grade}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Overall Financial Health
              </div>
            </div>
          </div>

          <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>Component Breakdown</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px' }}>Component</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Score</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Weight</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Current Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(healthScore.components).map(([key, comp]) => (
                <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '8px' }}>{comp.label}</td>
                  <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>{comp.score}/100</td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>{comp.weight}%</td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>{comp.value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>Key Strengths</h3>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {healthScore.strengths.map((strength, i) => (
                <li key={i} style={{ marginBottom: '5px', color: '#059669' }}>{strength}</li>
              ))}
            </ul>
          </div>

          {healthScore.improvements.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>Areas for Improvement</h3>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {healthScore.improvements.map((improvement, i) => (
                  <li key={i} style={{ marginBottom: '5px', color: '#dc2626' }}>{improvement}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Budget Impact */}
      {budgetData && (
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
            Budget Impact Analysis
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '8px' }}>Monthly Income</td>
                <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                  {formatCurrency(budgetData.monthlyIncome)}
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '8px' }}>Total Monthly Debt</td>
                <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                  {formatCurrency(budgetData.totalMonthlyDebt)}
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '8px' }}>Disposable Income (After Mortgage)</td>
                <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                  {formatCurrency(budgetData.disposableIncomeAfter)}
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '8px' }}>Savings Rate</td>
                <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                  {budgetData.savingsRate.toFixed(1)}%
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {/* Loan Programs */}
      {loanPrograms && loanPrograms.length > 0 && (
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
            Recommended Loan Programs
          </h2>
          {loanPrograms.slice(0, 3).map((program, index) => (
            <div key={index} style={{ marginBottom: '15px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>{program.name}</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>{program.description}</p>
              <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
                <span>Monthly: <strong>{formatCurrency(program.monthlyPayment)}</strong></span>
                <span>Rate: <strong>{program.interestRate}%</strong></span>
                <span>Score: <strong>{program.score}/100</strong></span>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Action Items */}
      {healthScore && healthScore.actionItems.length > 0 && (
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
            Recommended Actions
          </h2>
          {healthScore.actionItems.map((item, index) => (
            <div key={index} style={{ marginBottom: '15px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '5px' }}>
                <span style={{
                  padding: '2px 8px',
                  background: item.priority === 'high' ? '#fee2e2' : item.priority === 'medium' ? '#fef3c7' : '#e0e7ff',
                  color: item.priority === 'high' ? '#991b1b' : item.priority === 'medium' ? '#92400e' : '#3730a3',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                }}>
                  {item.priority}
                </span>
              </div>
              <p style={{ fontSize: '14px', marginBottom: '5px', fontWeight: 'bold' }}>{item.action}</p>
              <p style={{ fontSize: '13px', color: '#6b7280' }}>
                Impact: {item.impact} • Timeline: {item.timeframe}
              </p>
            </div>
          ))}
        </section>
      )}

      {/* Footer */}
      <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '2px solid #e5e7eb', fontSize: '12px', color: '#6b7280' }}>
        <p>This report is generated by EquiForge™ Supreme Intelligence and is for informational purposes only.</p>
        <p style={{ marginTop: '5px' }}>Consult with a financial advisor for personalized advice.</p>
      </div>
    </div>
  );
};
